import { useEffect, useMemo, useRef, useState } from 'react';
import { debounce } from 'lodash';

import { useInitGooglePlaceAutocompleteService } from '~components/atoms/custom/autocomplete/AutoCompleteGoogleMapsWithDetail/hooks/useInitGooglePlaceAutocompleteService';
import { PlaceType } from '~components/atoms/custom/autocomplete/AutoCompleteGoogleMapsWithDetail/type';

type FetchPlacesCallback = (
  a: google.maps.places.AutocompletePrediction[] | null,
  b: google.maps.places.PlacesServiceStatus,
) => void;

export function useGooglePlacesAutocompleteService() {
  const { autocompleteService } = useInitGooglePlaceAutocompleteService();

  const [selectedOption, setSelectedOption] = useState<PlaceType | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const [placeOptions, setPlaceOptions] = useState<readonly PlaceType[]>([]);
  const [isFetchingPlaces, setIsFetchingPlaces] = useState(false);

  const previousPlacesCache = useRef<Map<string, PlaceType[]>>(new Map());

  const fetchPlaces = useMemo(
    () =>
      debounce(
        async (
          request: google.maps.places.AutocompletionRequest,
          callback: FetchPlacesCallback,
        ) => {
          const cached = previousPlacesCache.current.get(request.input || '');
          if (cached) {
            callback(cached, google.maps.places.PlacesServiceStatus.OK);
            return;
          }

          setIsFetchingPlaces(true);

          const getPredictions = async (types: string[]) => {
            return new Promise<PlaceType[]>((resolve) => {
              if (!autocompleteService) {
                resolve([]);
                return;
              }

              autocompleteService.getPlacePredictions(
                { ...request, types },
                (preds, status) => {
                  if (status !== google.maps.places.PlacesServiceStatus.OK) {
                    resolve([]);
                  } else {
                    resolve(preds?.map((p) => ({ ...p })) || []);
                  }
                },
              );
            });
          };

          try {
            const [places, addresses] = await Promise.all([
              getPredictions(['establishment']),
              getPredictions(['address']),
            ]);

            const merged: PlaceType[] = [
              ...places.map((p) => ({ ...p, type: 'place' })),
              ...addresses
                .filter(
                  (addr) => !places.some((p) => p.place_id === addr.place_id),
                )
                .map((p) => ({ ...p, type: 'address' })),
            ];

            previousPlacesCache.current.set(request.input || '', merged);
            callback(
              merged.length ? merged : null,
              google.maps.places.PlacesServiceStatus.OK,
            );
          } finally {
            setIsFetchingPlaces(false);
          }
        },
        400,
      ),
    [autocompleteService],
  );

  /**
   * Searching input
   */
  useEffect(() => {
    if (!autocompleteService) return;

    let active = true;

    if (searchValue === '') {
      setPlaceOptions(selectedOption ? [selectedOption] : []);
      return;
    }

    const fetchPlacesRequest = { input: searchValue };
    const fetchPlacesCallback: FetchPlacesCallback = (results) => {
      if (active) {
        let newOptions: readonly PlaceType[] = [];

        if (selectedOption) {
          newOptions = [selectedOption];
        }

        if (results) {
          newOptions = [...newOptions, ...results];
        }

        setPlaceOptions(newOptions);
      }
      setIsFetchingPlaces(false);
    };

    fetchPlaces(fetchPlacesRequest, fetchPlacesCallback);

    return () => {
      active = false;
    };
  }, [selectedOption, searchValue, autocompleteService, fetchPlaces]);

  return {
    searchValue,
    setSearchValue,
    placeOptions,
    setPlaceOptions,
    isFetchingPlaces,
    selectedOption,
    setSelectedOption,
  };
}
